import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebarConfig } from './SidebarConfig';
import { useAuth } from '../hooks/useAuth';
import { logout } from '../utils/auth';

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(30, 58, 138, 0.3);
    border-radius: 3px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(59, 130, 246, 0.6);
    border-radius: 3px;
    transition: background 0.2s ease;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(59, 130, 246, 0.8);
  }
  
  .custom-scrollbar {
    scroll-behavior: smooth;
  }
  
  /* Responsive layout fixes */
  @media (max-width: 768px) {
    .sidebar-open-mobile {
      position: fixed !important;
      top: 56px !important;
      left: 0 !important;
      width: 100vw !important;
      height: calc(100vh - 56px) !important;
      z-index: 50 !important;
    }
    
    .main-content-mobile {
      margin-left: 0 !important;
      width: 100vw !important;
      max-width: 100vw !important;
    }
  }
`;

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    const locked = localStorage.getItem('sidebarLocked');
    // If sidebar is locked, always keep it open
    if (locked === 'true') {
      return true;
    }
    return saved ? JSON.parse(saved) : false;
  });
  const [sidebarLocked, setSidebarLocked] = useState(() => {
    const saved = localStorage.getItem('sidebarLocked');
    return saved ? JSON.parse(saved) : false;
  });
  const [unlockingSidebar, setUnlockingSidebar] = useState(false);
  const [togglingSidebar, setTogglingSidebar] = useState(false);
  // Initialize expandedItems from localStorage or use default expanded state from items
  const [expandedItems, setExpandedItems] = useState<{ [key: number]: boolean }>(() => {
    // Try to load from localStorage
    const saved = localStorage.getItem('sidebarExpandedItems');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {};
      }
    }
    return {};
  });
  const navigate = useNavigate();

  // Use centralized sidebar config
  const { userAuth } = useAuth();
  const { sidebarItems, sidebarTitleButton } = useSidebarConfig(userAuth, () => {
    // Close sidebar when navigating to a new page (if not locked)
    if (!sidebarLocked && sidebarOpen) {
      setSidebarOpen(false);
    }
  });



  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  useEffect(() => {
    localStorage.setItem('sidebarLocked', JSON.stringify(sidebarLocked));
  }, [sidebarLocked]);

  // Save expandedItems to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarExpandedItems', JSON.stringify(expandedItems));
  }, [expandedItems]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('[data-sidebar]') as HTMLElement;
      const target = event.target as HTMLElement;
      if (target.closest('button') && target.textContent?.includes('☰')) {
        return;
      }
      // Don't close sidebar if it's locked
      if (sidebar && !sidebar.contains(target) && sidebarOpen && !sidebarLocked && !unlockingSidebar && !togglingSidebar) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen, sidebarLocked, unlockingSidebar, togglingSidebar]);

  // Handle logo click based on user role
  const handleLogoClick = () => {
    if (!userAuth) {
      // If not logged in, go to login page
      navigate('/');
      return;
    }

    // Navigate based on user role
    switch (userAuth.role) {
      case 'admin':
        navigate('/HomeScreen_AdminPanel');
        break;
      case 'accountant':
        navigate('/main_accountant');
        break;

      default:
        // Default to login page for unknown roles
        navigate('/');
        break;
    }
  };

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="min-h-screen bg-gray-100 text-gray-900 font-sans transition-all duration-300">
      {/* Top Bar */}
      <div className="w-full flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 shadow-lg z-40 relative">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setTogglingSidebar(true);
              setTimeout(() => setTogglingSidebar(false), 100);
              if (sidebarLocked) {
                // If sidebar is locked, don't allow closing it
                return;
              }
              if (!sidebarOpen) {
                setSidebarOpen(true);
              } else {
                setSidebarOpen(false);
              }
            }}
            className={`text-lg font-medium transition-all duration-200 p-1 rounded-lg ${
              sidebarLocked 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'hover:text-blue-200 hover:bg-white/10'
            }`}
            disabled={sidebarLocked}
          >
            ☰
          </button>
          <div 
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity duration-200"
            onClick={handleLogoClick}
          >
            <img 
              src={process.env.PUBLIC_URL + '/favicon.ico'} 
              alt="NovaQ Logo" 
              className="w-6 h-6" 
              onError={(e) => {
                // Prevent infinite loop by hiding the image if it fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }} 
            />
            <div className="text-base font-semibold tracking-wide">NovaQ</div>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="w-7 h-7 bg-white/20 backdrop-blur-sm text-white rounded-full flex items-center justify-center font-semibold shadow-sm hover:bg-white/30 transition-all duration-200 border border-white/20"
          >
            P
          </button>
          {showProfileMenu && (
            <div className="absolute right-0 mt-1 bg-white/95 backdrop-blur-sm text-sm text-gray-800 shadow-xl rounded-lg py-1 z-10 w-32 transition-all duration-200 border border-gray-200">
              <button
                onClick={() => navigate('/settings')}
                className="block px-3 py-1.5 hover:bg-gray-100 w-full text-left rounded-sm transition-colors"
              >
                ⚙️ Тохиргоо
              </button>
              <button
                onClick={() => logout()}
                className="block px-3 py-1.5 hover:bg-gray-100 w-full text-left rounded-sm transition-colors"
              >
                🔓 Гарах
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Sidebar */}
      <div
        data-sidebar
        className={`fixed top-[56px] left-0 z-30 h-[calc(100vh-56px)] bg-blue-700 text-white p-5 shadow-xl rounded-tr-2xl rounded-br-2xl transform transition-transform duration-200 ease-out flex flex-col
        ${(sidebarOpen || sidebarLocked) ? 'translate-x-0 sidebar-open-mobile' : '-translate-x-full'}`}
        style={{ width: '15rem', minHeight: 'calc(100vh - 56px)' }}
      >
        <div className="flex items-center justify-between mb-3">
          {sidebarTitleButton && (
            <button
              className="text-lg font-bold text-white hover:text-blue-200 transition-colors duration-150 focus:outline-none"
              onClick={sidebarTitleButton.onClick}
              style={{ background: 'none', border: 'none', padding: 0 }}
            >
              {sidebarTitleButton.label}
            </button>
          )}
          <button
            onClick={() => {
              if (sidebarLocked) {
                setUnlockingSidebar(true);
                setSidebarLocked(false);
                setTimeout(() => setUnlockingSidebar(false), 300);
              } else {
                setSidebarLocked(true);
                setSidebarOpen(true); // Ensure sidebar is open when locking
              }
            }}
            className={`p-1 rounded transition-all duration-150 ${sidebarLocked ? 'bg-blue-600 text-yellow-300' : 'bg-blue-600/50 text-white hover:bg-blue-600'}`}
            title={sidebarLocked ? 'Түгжээг нээх' : 'Түгжээтэй болгох'}
          >
            {sidebarLocked ? '🔒' : '🔓'}
          </button>
        </div>
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Filter..."
              className="w-full px-3 py-2 bg-blue-600/50 border border-blue-500/50 rounded-lg text-white placeholder-blue-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            />
            <span className="absolute right-3 top-2.5 text-blue-200">🔍</span>
          </div>
        </div>
        <div 
          className="space-y-2 text-sm overflow-y-auto flex-1 custom-scrollbar" 
          style={{ 
            maxHeight: 'calc(100vh - 200px)'
          }}
        >
          {sidebarItems ? (
            sidebarItems.map((item, idx) => {
              // Use saved expanded state, or default to item.isExpanded, or true if item has children
              const isExpanded = expandedItems[idx] !== undefined 
                ? expandedItems[idx] 
                : (item.isExpanded !== undefined ? item.isExpanded : (item.children && item.children.length > 0));
              
              if (item.children && item.children.length > 0) {
                return (
                  <div key={idx} className="space-y-1">
                    <div
                      className="cursor-pointer hover:text-blue-200 transition-colors duration-150 flex items-center justify-between p-2 rounded hover:bg-blue-600/50"
                      onClick={() => {
                        setExpandedItems(prev => ({
                          ...prev,
                          [idx]: !isExpanded
                        }));
                      }}
                    >
                      <div className="flex items-center">
                        {item.icon && <span className="mr-2">{item.icon}</span>}
                        {item.label}
                      </div>
                      <span className="text-xs transition-transform duration-200">
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="ml-4 space-y-1 border-l border-blue-600 pl-3">
                        {item.children.map((child, childIdx) => (
                          <div
                            key={childIdx}
                            className="cursor-pointer hover:text-blue-200 transition-colors duration-150 flex items-center py-1 px-2 rounded hover:bg-blue-600/50"
                            onClick={child.onClick}
                          >
                            {child.icon && <span className="mr-2 text-sm">{child.icon}</span>}
                            <span className="text-sm">{child.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <div
                    key={idx}
                    className="cursor-pointer hover:text-blue-200 transition-colors duration-150 flex items-center py-1 px-2 rounded hover:bg-blue-600/50"
                    onClick={item.onClick}
                  >
                    {item.icon && <span className="mr-2">{item.icon}</span>}
                    {item.label}
                  </div>
                );
              }
            })
          ) : null}
        </div>
        {/* Version Info */}
        <div className="text-xs text-blue-200 space-y-1 mt-auto pt-4">
          <div>NovaQ систем</div>
          <div>Version 1.0.1</div>
        </div>
      </div>
      {/* Main Content */}
      <div className={`flex-1 min-h-[calc(100vh-56px)] flex flex-col items-center justify-start transition-all duration-200 ease-out ${
        (sidebarOpen || sidebarLocked) ? 'ml-60' : 'ml-0'
      }`}>
        <div className={`px-2 sm:px-6 pt-2 pb-4 transition-all duration-200 ease-out main-content-mobile ${
          (sidebarOpen || sidebarLocked) ? 'w-[calc(100vw-240px)] max-w-[calc(100vw-240px)]' : 'w-full max-w-full'
        }`}>
          {children}
        </div>
      </div>
    </div>
    </>
  );
};

export default MainLayout; 